/*
Domain-validity mask for the interactive forecast map.

Regional models (AROME, the ARPEGE Europe crop, …) only cover their own
domain rectangle, but nothing on the basemap says so: with a mostly
transparent variable like precipitation a dry frame's edge is invisible,
and the untouched world around the domain reads as "no rain anywhere"
instead of "no data here". The mask greys out everything OUTSIDE the
domain — a world-covering polygon with the domain cut out as a hole — so
the valid area is the only part of the map left at full strength.

Pure geometry + shared styling constants (no MapLibre imports) so the
webapp (react-map-gl) and the mobile app (MapLibre RN) drape the exact
same mask: both feed the feature into a geojson source and anchor the
fill at the first symbol layer — above the borders and the coastline
(they grey out with the rest of the outside world) but below the place
labels, which stay readable.
*/

// Muted grey over positron's light land/water: strong enough that the
// domain edge reads at a glance, light enough that the outside world
// (coasts, borders) stays recognisable underneath. Tuned on device next
// to the boosted border slates (see transformForecastBasemapStyle).
export const FORECAST_DOMAIN_MASK_COLOR = 'hsl(0, 0%, 55%)';
export const FORECAST_DOMAIN_MASK_OPACITY = 0.45;

// Shared source/layer ids so both platforms (and their tests) agree.
export const FORECAST_DOMAIN_MASK_SOURCE_ID = 'forecast-domain-mask';
export const FORECAST_DOMAIN_MASK_LAYER_ID = 'forecast-domain-mask-layer';

// A domain spanning (almost) every longitude is a global model: there is
// no "outside" to grey out, and a hole touching the outer ring would be
// degenerate geometry anyway. Slack absorbs grids like [-179.75, 180].
const GLOBAL_LON_SPAN = 359;

export type ForecastMapDomainMask = {
  type: 'Feature';
  properties: Record<string, never>;
  geometry: {
    type: 'Polygon';
    coordinates: [number, number][][];
  };
};

// The model's domain footprint as GeoJSON (from the manifest's
// `overlay.footprint`). A limited-area model's real domain is a curved
// sub-region of its lat/lon box — cutting the mask to it, rather than the
// rectangle, stops the bright basemap wedge in the bbox corners.
export type ForecastMapDomainFootprint =
  | {type: 'Polygon'; coordinates: number[][][]}
  | {type: 'MultiPolygon'; coordinates: number[][][][]};

// Exterior ring(s) of a footprint geometry — the mask holes. Interior rings
// (rare for a domain) are dropped: the mask only needs the outer boundary.
// Returns null for an absent or malformed footprint so the caller falls back
// to the rectangular hole.
const footprintHoles = (
  footprint: ForecastMapDomainFootprint | null | undefined,
): [number, number][][] | null => {
  if (!footprint) {
    return null;
  }
  const polygons =
    footprint.type === 'Polygon' ? [footprint.coordinates] : footprint.coordinates;
  const rings = polygons
    .map((polygon) => polygon[0])
    .filter((ring): ring is number[][] => Array.isArray(ring) && ring.length >= 4)
    .map((ring) => ring as [number, number][]);
  return rings.length > 0 ? rings : null;
};

/**
 * GeoJSON mask feature for a run's domain: a world-covering outer ring with
 * the domain cut out as hole(s).
 *
 * `bounds` ([west, south, east, north]) is the overlay's rectangular extent.
 * When a `footprint` is given (the model's true, often curved domain) its
 * ring(s) are the hole so the mask hugs the real shape; otherwise the bounds
 * rectangle is the hole (unchanged behaviour).
 *
 * Returns null for global domains (and missing bounds): global models render
 * exactly as before, no mask mounted.
 */
export const forecastMapDomainMask = (
  bounds: [number, number, number, number] | null | undefined,
  footprint?: ForecastMapDomainFootprint | null,
): ForecastMapDomainMask | null => {
  if (!bounds) {
    return null;
  }
  const [west, south, east, north] = bounds;
  if (east - west >= GLOBAL_LON_SPAN) {
    return null;
  }
  const holes: [number, number][][] = footprintHoles(footprint) ?? [
    // Hole: the domain rectangle, clockwise (opposite winding, as the GeoJSON
    // spec asks for interior rings).
    [
      [west, south],
      [west, north],
      [east, north],
      [east, south],
      [west, south],
    ],
  ];
  return {
    type: 'Feature',
    properties: {},
    geometry: {
      type: 'Polygon',
      coordinates: [
        // Outer ring: the whole world, counter-clockwise. ±90 overshoots
        // Web Mercator's ±85 rendering limit — harmless, always clipped.
        [
          [-180, -90],
          [180, -90],
          [180, 90],
          [-180, 90],
          [-180, -90],
        ],
        ...holes,
      ],
    },
  };
};
